import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, User, Briefcase, IndianRupee, Calendar, Ban } from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  invoiced: 'bg-blue-100 text-blue-700',
  completed: 'bg-violet-100 text-violet-700',
  cancelled: 'bg-muted text-muted-foreground',
};

export default function PlacementsPage() {
  const [placements, setPlacements] = useState([]);
  const [clients, setClients] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    client_id: '',
    candidate_name: '',
    position: '',
    offered_ctc: '',
    billing_percentage: '',
    recruiter_id: '',
    joining_date: '',
    guarantee_period_days: '90',
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [placementsRes, clientsRes, usersRes] = await Promise.all([
        api.get('/finance/placements'),
        api.get('/finance/clients'),
        api.get('/employees/all/users'),
      ]);
      setPlacements(placementsRes.data);
      setClients(clientsRes.data.filter(c => c.is_active));
      setRecruiters(usersRes.data.filter(u => u.role === 'employee'));
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = placements.filter(p =>
    p.candidate_name.toLowerCase().includes(search.toLowerCase()) ||
    p.position.toLowerCase().includes(search.toLowerCase()) ||
    p.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setForm({
      client_id: '',
      candidate_name: '',
      position: '',
      offered_ctc: '',
      billing_percentage: '',
      recruiter_id: '',
      joining_date: '',
      guarantee_period_days: '90',
    });
    setDialogOpen(true);
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setForm(prev => ({
      ...prev,
      client_id: clientId,
      billing_percentage: client?.billing_percentage?.toString() || prev.billing_percentage,
      guarantee_period_days: client?.replacement_clause_days?.toString() || prev.guarantee_period_days,
    }));
  };

  const handleSave = async () => {
    if (!form.client_id || !form.candidate_name || !form.position || !form.offered_ctc || !form.billing_percentage || !form.recruiter_id || !form.joining_date) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.post('/finance/placements', {
        ...form,
        offered_ctc: parseFloat(form.offered_ctc),
        billing_percentage: parseFloat(form.billing_percentage),
        guarantee_period_days: parseInt(form.guarantee_period_days),
      });
      toast.success('Placement created');
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create placement');
    } finally {
      setSaving(false);
    }
  };

  const cancelPlacement = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this placement?')) return;
    try {
      await api.put(`/finance/placements/${id}/cancel`);
      toast.success('Placement cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const calculatedValue = form.offered_ctc && form.billing_percentage 
    ? parseFloat(form.offered_ctc) * parseFloat(form.billing_percentage) / 100 
    : 0;

  return (
    <div data-testid="placements-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Placements</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Manage candidate placements</p>
        </div>
        <Button onClick={openAdd} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="add-placement-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Placement
        </Button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search placements..." className="pl-10 rounded-sm" />
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Candidate</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Position</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">CTC</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Invoice Value</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Recruiter</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Joining Date</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No placements found</TableCell></TableRow>
            ) : filtered.map(p => (
              <TableRow key={p.id} data-testid={`placement-row-${p.id}`}>
                <TableCell className="font-medium">{p.candidate_name}</TableCell>
                <TableCell>{p.position}</TableCell>
                <TableCell>{p.client_name}</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(p.offered_ctc)}</TableCell>
                <TableCell className="text-right font-mono text-primary font-medium">{formatCurrency(p.invoice_value)}</TableCell>
                <TableCell>{p.recruiter_name}</TableCell>
                <TableCell>{p.joining_date}</TableCell>
                <TableCell>
                  <Badge className={STATUS_COLORS[p.status]}>{p.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  {p.status === 'active' && (
                    <Button variant="ghost" size="sm" onClick={() => cancelPlacement(p.id)} className="text-red-600">
                      <Ban className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Placement</DialogTitle>
            <DialogDescription>Record a new candidate placement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Client *</Label>
              <Select value={form.client_id} onValueChange={handleClientChange}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}{c.state ? ` - ${c.state}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Candidate Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={form.candidate_name} onChange={e => set('candidate_name', e.target.value)} className="pl-10 rounded-sm" placeholder="John Doe" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Position *</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={form.position} onChange={e => set('position', e.target.value)} className="pl-10 rounded-sm" placeholder="Software Engineer" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Offered CTC *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" value={form.offered_ctc} onChange={e => set('offered_ctc', e.target.value)} className="pl-10 rounded-sm" placeholder="1200000" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Billing % *</Label>
                <Input type="number" value={form.billing_percentage} onChange={e => set('billing_percentage', e.target.value)} className="rounded-sm" placeholder="8.33" />
              </div>
            </div>

            {calculatedValue > 0 && (
              <div className="bg-primary/5 p-3 rounded-sm">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Calculated Invoice Value</p>
                <p className="text-xl font-mono font-bold text-primary">{formatCurrency(calculatedValue)}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Recruiter *</Label>
              <Select value={form.recruiter_id} onValueChange={v => set('recruiter_id', v)}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select recruiter" />
                </SelectTrigger>
                <SelectContent>
                  {recruiters.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.employee_id})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Joining Date *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="date" value={form.joining_date} onChange={e => set('joining_date', e.target.value)} className="pl-10 rounded-sm" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Guarantee Days</Label>
                <Input type="number" value={form.guarantee_period_days} onChange={e => set('guarantee_period_days', e.target.value)} className="rounded-sm" placeholder="90" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 rounded-sm">
              {saving ? 'Creating...' : 'Create Placement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
